"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

const IMAGE_BASE = "/image/new-version/casting/Vacuum-Casting/Process";

const STEPS = [
	{
		id: "step1",
		titleKey: "urethaneProcessStep1Title",
		descKey: "urethaneProcessStep1Desc",
		image: `${IMAGE_BASE}/what's-the-urethane-casting-process-1.webp`,
		altKey: "urethaneProcessStep1Alt",
	},
	{
		id: "step2",
		titleKey: "urethaneProcessStep2Title",
		descKey: "urethaneProcessStep2Desc",
		image: `${IMAGE_BASE}/what's-the-urethane-casting-process-2.webp`,
		altKey: "urethaneProcessStep2Alt",
	},
	{
		id: "step3",
		titleKey: "urethaneProcessStep3Title",
		descKey: "urethaneProcessStep3Desc",
		image: `${IMAGE_BASE}/what's-the-urethane-casting-process-1.webp`,
		altKey: "urethaneProcessStep3Alt",
	},
] as const;

type StepId = (typeof STEPS)[number]["id"];

export default function UrethaneCastingProcess() {
	const t = useTranslations("new-vacuum-casting");
	const [openId, setOpenId] = useState<StepId>("step2");

	const activeStep = STEPS.find((s) => s.id === openId) ?? STEPS[0];

	return (
		<section className="bg-[#ffffff] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("urethaneProcessTitle")}
					</h2>
					<p className="mx-auto mt-3 max-w-4xl text-sm leading-relaxed text-black sm:mt-4 md:text-base">
						{t("urethaneProcessDesc")}
					</p>
				</header>

				<div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2 lg:gap-10">
					<ol className="relative">
						{STEPS.map((step, index) => {
							const isOpen = openId === step.id;
							const isLast = index === STEPS.length - 1;
							return (
								<li key={step.id} className="relative pl-14 sm:pl-16">
									{!isLast && (
										<span
											className="absolute left-5 top-10 h-[calc(100%-2.5rem)] w-px bg-gray-300 sm:left-6"
											aria-hidden
										/>
									)}
									<span className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-full bg-themePrimary400 text-base font-bold text-white sm:h-12 sm:w-12 sm:text-lg">
										{index + 1}
									</span>
									<div className="border-b border-[#828282] pb-4">
										<button
											type="button"
											onClick={() => setOpenId(step.id)}
											className="flex w-full items-center justify-between gap-4 py-2 text-left"
											aria-expanded={isOpen}
										>
											<span className="text-base font-bold text-black sm:text-lg">{t(step.titleKey)}</span>
											<span className="flex h-7 w-7 shrink-0 items-center justify-center text-black">
												{isOpen ? (
													<CaretDown className="h-4 w-4" weight="bold" aria-hidden />
												) : (
													<CaretRight className="h-4 w-4" weight="bold" aria-hidden />
												)}
											</span>
										</button>
										<div
											className={`grid transition-all duration-300 ease-in-out ${
												isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
											}`}
										>
											<div className="min-h-0 overflow-hidden">
												<p className="pb-2 pt-2 text-sm leading-relaxed text-black md:text-base">
													{t(step.descKey)}
												</p>
											</div>
										</div>
									</div>
								</li>
							);
						})}
					</ol>

					<div className="relative aspect-[4/3] w-full overflow-hidden bg-[#bfbfbf] lg:aspect-auto lg:h-full lg:min-h-[360px]">
						<Image
							src={activeStep.image}
							alt={t(activeStep.altKey)}
							fill
							className="object-cover"
							sizes="(max-width: 1024px) 100vw, 50vw"
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
